/**
 * m3 echo Frontend - Audit Log Tab
 *
 * Displays paginated audit log entries with filtering, expandable details,
 * and CSV export functionality.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  IconButton,
  Collapse,
  Chip,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import DownloadIcon from '@mui/icons-material/Download';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import {
  auditLogApi,
  userApi,
  AuditLogEntry,
  AuditLogFilters,
  UserWithTenant,
} from '../../services/admin';

/**
 * Format timestamp for display (converts UTC to local time, German format)
 */
const formatTimestamp = (dateStr: string): string => {
  let date: Date;
  if (dateStr.endsWith('Z') || dateStr.includes('+') || dateStr.includes('-', 10)) {
    date = new Date(dateStr);
  } else {
    date = new Date(dateStr + 'Z');
  }
  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = date.getFullYear();
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');
  const seconds = date.getSeconds().toString().padStart(2, '0');
  return `${day}.${month}.${year} ${hours}:${minutes}:${seconds}`;
};

/**
 * Expandable row component for audit log entries
 */
const AuditLogRow: React.FC<{ entry: AuditLogEntry }> = ({ entry }) => {
  const [open, setOpen] = useState(false);
  const hasDetails = entry.details && Object.keys(entry.details).length > 0;

  return (
    <>
      <TableRow hover sx={{ '& > *': { borderBottom: hasDetails ? 'unset' : undefined } }}>
        <TableCell padding="checkbox">
          {hasDetails && (
            <IconButton size="small" onClick={() => setOpen(!open)}>
              {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </IconButton>
          )}
        </TableCell>
        <TableCell sx={{ whiteSpace: 'nowrap' }}>
          {formatTimestamp(entry.created_at)}
        </TableCell>
        <TableCell>{entry.user_email || '—'}</TableCell>
        <TableCell>
          <Chip label={entry.action} size="small" variant="outlined" />
        </TableCell>
        <TableCell>{entry.resource_type || '—'}</TableCell>
        <TableCell>{entry.resource_id ?? '—'}</TableCell>
        <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
          {entry.ip_address || '—'}
        </TableCell>
      </TableRow>
      {hasDetails && (
        <TableRow>
          <TableCell colSpan={7} sx={{ py: 0 }}>
            <Collapse in={open} timeout="auto" unmountOnExit>
              <Box sx={{ m: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Details
                </Typography>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    bgcolor: 'grey.50',
                    fontFamily: 'monospace',
                    fontSize: '0.8rem',
                    maxHeight: 300,
                    overflow: 'auto',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-all',
                  }}
                >
                  {JSON.stringify(entry.details, null, 2)}
                </Paper>
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  );
};

export const AuditTab: React.FC = () => {
  // Data state
  const [entries, setEntries] = useState<AuditLogEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Filter options (loaded from API)
  const [actionOptions, setActionOptions] = useState<string[]>([]);
  const [resourceTypeOptions, setResourceTypeOptions] = useState<string[]>([]);
  const [users, setUsers] = useState<UserWithTenant[]>([]);

  // Active filters
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [selectedUser, setSelectedUser] = useState<number | ''>('');
  const [selectedAction, setSelectedAction] = useState<string>('');
  const [selectedResourceType, setSelectedResourceType] = useState<string>('');
  const [dateFrom, setDateFrom] = useState<string>('');
  const [dateTo, setDateTo] = useState<string>('');

  // Export state
  const [exporting, setExporting] = useState(false);

  /**
   * Build filters object from current state
   */
  const buildFilters = useCallback((): AuditLogFilters => {
    const filters: AuditLogFilters = {
      page: page + 1, // API uses 1-based pages
      page_size: pageSize,
    };
    if (selectedUser !== '') filters.user_id = selectedUser;
    if (selectedAction) filters.action = selectedAction;
    if (selectedResourceType) filters.resource_type = selectedResourceType;
    if (dateFrom) filters.date_from = dateFrom;
    if (dateTo) filters.date_to = dateTo;
    return filters;
  }, [page, pageSize, selectedUser, selectedAction, selectedResourceType, dateFrom, dateTo]);

  /**
   * Load audit log entries
   */
  const loadEntries = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await auditLogApi.getAuditLogs(buildFilters());
      setEntries(response.data.items || []);
      setTotal(response.data.total);
    } catch (err: any) {
      console.error('Fehler beim Laden der Audit-Logs:', err);
      setError(err.response?.data?.detail || 'Fehler beim Laden der Audit-Logs');
    } finally {
      setLoading(false);
    }
  }, [buildFilters]);

  /**
   * Load filter options (actions, resource types, users)
   */
  const loadFilterOptions = useCallback(async () => {
    try {
      const [actionsRes, usersRes] = await Promise.all([
        auditLogApi.getAuditActions(),
        userApi.getAll(),
      ]);
      setActionOptions(actionsRes.data.actions);
      setResourceTypeOptions(actionsRes.data.resource_types);
      setUsers(usersRes.data);
    } catch (err: any) {
      console.error('Fehler beim Laden der Filteroptionen:', err);
    }
  }, []);

  // Load filter options on mount
  useEffect(() => {
    loadFilterOptions();
  }, [loadFilterOptions]);

  // Reload entries when filters or pagination change
  useEffect(() => {
    loadEntries();
  }, [loadEntries]);

  /**
   * Handle CSV export
   */
  const handleExport = async () => {
    setExporting(true);
    try {
      const filters = buildFilters();
      // Remove pagination for export — export all matching entries
      delete filters.page;
      delete filters.page_size;
      const response = await auditLogApi.exportAuditCsv(filters);
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `audit-log-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      console.error('Fehler beim Exportieren:', err);
      setError(err.response?.data?.detail || 'Fehler beim Exportieren der Audit-Logs');
    } finally {
      setExporting(false);
    }
  };

  /**
   * Reset all filters to defaults
   */
  const handleResetFilters = () => {
    setSelectedUser('');
    setSelectedAction('');
    setSelectedResourceType('');
    setDateFrom('');
    setDateTo('');
    setPage(0);
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6">Audit-Protokoll</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={handleExport}
            disabled={exporting || loading}
          >
            {exporting ? 'Exportieren...' : 'CSV Exportieren'}
          </Button>
          <Button
            variant="contained"
            startIcon={<RefreshIcon />}
            onClick={loadEntries}
            disabled={loading}
          >
            Aktualisieren
          </Button>
        </Box>
      </Box>

      {/* Filter Bar */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            label="Von"
            type="date"
            size="small"
            value={dateFrom}
            onChange={(e) => { setDateFrom(e.target.value); setPage(0); }}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 160 }}
          />
          <TextField
            label="Bis"
            type="date"
            size="small"
            value={dateTo}
            onChange={(e) => { setDateTo(e.target.value); setPage(0); }}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 160 }}
          />
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Benutzer</InputLabel>
            <Select
              value={selectedUser}
              label="Benutzer"
              onChange={(e) => { setSelectedUser(e.target.value as number | ''); setPage(0); }}
            >
              <MenuItem value="">Alle</MenuItem>
              {users.map((u) => (
                <MenuItem key={u.id} value={u.id}>
                  {u.email}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Aktion</InputLabel>
            <Select
              value={selectedAction}
              label="Aktion"
              onChange={(e) => { setSelectedAction(e.target.value); setPage(0); }}
            >
              <MenuItem value="">Alle</MenuItem>
              {actionOptions.map((a) => (
                <MenuItem key={a} value={a}>
                  {a}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 170 }}>
            <InputLabel>Ressourcentyp</InputLabel>
            <Select
              value={selectedResourceType}
              label="Ressourcentyp"
              onChange={(e) => { setSelectedResourceType(e.target.value); setPage(0); }}
            >
              <MenuItem value="">Alle</MenuItem>
              {resourceTypeOptions.map((rt) => (
                <MenuItem key={rt} value={rt}>
                  {rt}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button size="small" onClick={handleResetFilters}>
            Filter zurücksetzen
          </Button>
        </Box>
      </Paper>

      {/* Error */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Loading */}
      {loading && (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <CircularProgress />
        </Box>
      )}

      {/* Table */}
      {!loading && (
        <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox" />
                <TableCell>Zeitstempel</TableCell>
                <TableCell>Benutzer</TableCell>
                <TableCell>Aktion</TableCell>
                <TableCell>Ressourcentyp</TableCell>
                <TableCell>Ressourcen-ID</TableCell>
                <TableCell>IP-Adresse</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {entries.length > 0 ? (
                entries.map((entry) => (
                  <AuditLogRow key={entry.id} entry={entry} />
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Typography color="text.secondary" sx={{ py: 2 }}>
                      Keine Audit-Einträge gefunden
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={pageSize}
            onRowsPerPageChange={(e) => {
              setPageSize(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[10, 25, 50, 100]}
            labelRowsPerPage="Einträge pro Seite:"
            labelDisplayedRows={({ from, to, count }) =>
              `${from}–${to} von ${count !== -1 ? count : `mehr als ${to}`}`
            }
          />
        </TableContainer>
      )}
    </Box>
  );
};

export default AuditTab;
